import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { EASE } from "@/components/Reveal";

const HERO_IMG =
    "https://images.unsplash.com/photo-1587050265310-1a2d98ccce5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxpbmZsdWVuY2VyJTIwbWFya2V0aW5nJTIwY3JlYXRvciUyMGNhbWVyYSUyMHByb2R1Y3Rpb24lMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTI1OTQxMTR8MA&ixlib=rb-4.1.0&q=85";

const LINES = ["Where", "attention", "converges", "into revenue."];

function ConvergenceCanvas() {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let raf, w, h;
        let particles = [];

        const resize = () => {
            w = canvas.offsetWidth;
            h = canvas.offsetHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const spawn = (p) => {
            const edge = Math.floor(Math.random() * 4);
            if (edge === 0) { p.x = Math.random() * w; p.y = -10; }
            else if (edge === 1) { p.x = w + 10; p.y = Math.random() * h; }
            else if (edge === 2) { p.x = Math.random() * w; p.y = h + 10; }
            else { p.x = -10; p.y = Math.random() * h; }
            p.speed = 0.35 + Math.random() * 1.1;
            p.size = 0.8 + Math.random() * 1.7;
            p.hot = Math.random() < 0.24;
            p.drift = (Math.random() - 0.5) * 0.9;
        };

        resize();
        const count = reduced ? 0 : Math.min(110, Math.floor(w / 11));
        for (let i = 0; i < count; i++) {
            const p = {};
            spawn(p);
            p.x = Math.random() * w;
            p.y = Math.random() * h;
            particles.push(p);
        }

        const tick = () => {
            ctx.clearRect(0, 0, w, h);
            const fx = w * 0.66;
            const fy = h * 0.4;
            for (const p of particles) {
                const dx = fx - p.x;
                const dy = fy - p.y;
                const d = Math.hypot(dx, dy) || 1;
                const nx = dx / d;
                const ny = dy / d;
                p.x += nx * p.speed - ny * p.drift * 0.3;
                p.y += ny * p.speed + nx * p.drift * 0.3;
                const alpha = Math.min(0.55, (d / (h * 0.55)) * 0.9);
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = p.hot
                    ? `rgba(255, 80, 110, ${alpha})`
                    : `rgba(226, 232, 240, ${alpha * 0.65})`;
                ctx.fill();
                if (d < 16) spawn(p);
            }
            raf = requestAnimationFrame(tick);
        };

        if (!reduced) tick();
        window.addEventListener("resize", resize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return <canvas ref={ref} className="absolute inset-0 h-full w-full" aria-hidden="true" />;
}

const lineVariants = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1, ease: EASE, delay: 0.2 + i * 0.14 },
    }),
};

export default function Hero() {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 55, damping: 18 });
    const sy = useSpring(my, { stiffness: 55, damping: 18 });
    const rotX = useTransform(sy, [-0.5, 0.5], [5, -5]);
    const rotY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
    const shiftX = useTransform(sx, [-0.5, 0.5], [-10, 10]);
    const shiftY = useTransform(sy, [-0.5, 0.5], [-8, 8]);

    const onMouseMove = (e) => {
        mx.set(e.clientX / window.innerWidth - 0.5);
        my.set(e.clientY / window.innerHeight - 0.5);
    };

    return (
        <section
            onMouseMove={onMouseMove}
            className="relative flex min-h-screen items-center overflow-hidden bg-ink pt-20 pb-10 sm:pt-20"
            data-testid="hero-section"
        >
            <ConvergenceCanvas />
            <div className="hero-glow pointer-events-none absolute inset-0" aria-hidden="true" />
            <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy/90 to-transparent"
                aria-hidden="true"
            />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
                <div className="lg:col-span-8">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/12 bg-white/5 px-4 py-2 backdrop-blur"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                        </span>
                        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">
                            Accepting new brand campaigns
                        </span>
                    </motion.div>

                    <h1 className="font-syne text-[clamp(2rem,5.4vw,4.4rem)] font-extrabold uppercase leading-[0.98] tracking-tight">
                        {LINES.map((line, i) => (
                            <span key={line} className="-mb-[0.1em] block overflow-hidden pb-[0.1em]">
                                <motion.span
                                    custom={i}
                                    variants={lineVariants}
                                    initial="hidden"
                                    animate="show"
                                    className={`block ${
                                        i === 2
                                            ? "bg-gradient-to-r from-crimson to-flame bg-clip-text text-transparent"
                                            : ""
                                    }`}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
                    >
                        The integrated influencer + performance marketing engine behind 1B+
                        campaign views. 3,200+ vetted creators, one team, and full-funnel
                        accountability from organic reach to ROAS.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#contact"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-crimson to-flame px-7 py-4 text-sm font-semibold text-white shadow-xl shadow-crimson/30 transition-transform duration-300 hover:scale-[1.03]"
                        >
                            Start a Campaign
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                        <a
                            href="#engine"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-white/40 hover:bg-white/5"
                        >
                            Explore the Engine
                            <ArrowDown className="h-4 w-4" />
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
                    className="lg:col-span-4"
                    style={{ perspective: 1000 }}
                >
                    <motion.div
                        style={{ rotateX: rotX, rotateY: rotY, x: shiftX, y: shiftY }}
                        className="relative mx-auto max-w-md lg:max-w-none"
                    >
                        <div className="relative overflow-hidden rounded-[2rem] border border-white/12 shadow-2xl shadow-black/60">
                            <img
                                src={HERO_IMG}
                                alt="Creator filming a brand campaign in studio"
                                className="h-[360px] w-full object-cover sm:h-[420px] lg:h-[440px]"
                                loading="eager"
                            />
                            <div
                                className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-ink/70 via-transparent to-crimson/25"
                                aria-hidden="true"
                            />
                            <div
                                className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-ink/80 to-transparent"
                                aria-hidden="true"
                            />
                        </div>

                        <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                            className="absolute -left-4 top-8 rounded-2xl border border-white/15 bg-ink/70 px-5 py-4 backdrop-blur-xl sm:-left-8"
                        >
                            <p className="font-syne text-2xl font-extrabold text-white">3,200+</p>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                Vetted Creators
                            </p>
                        </motion.div>

                        <motion.div
                            animate={{ y: [0, 10, 0] }}
                            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 0.8 }}
                            className="absolute -right-4 bottom-10 rounded-2xl border border-white/15 bg-ink/70 px-5 py-4 backdrop-blur-xl sm:-right-8"
                        >
                            <p className="font-syne text-2xl font-extrabold text-white">1B+</p>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                Campaign Views
                            </p>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
